import { baseUrl } from '../constants/Connections'

const STORAGE_KEY = 'yearAnalyticsAccess:v1'
const MAX_AGE = 24 * 60 * 60 * 1000
let cached = null
let activeKey = null
const pending = new Map()
const listeners = new Map()

export const yearAnalyticsAccessKey = (user) => user?.id
    ? JSON.stringify([baseUrl, user.id, user.institution_id ?? null, user.role ?? null])
    : null

const readStorage = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) } catch (_) { return null }
}

const writeStorage = (value) => {
    try {
        if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
        else localStorage.removeItem(STORAGE_KEY)
    } catch (_) { /* Memory caching still works when browser storage is unavailable. */ }
}

export function readYearAnalyticsAccess(key) {
    if (!key) return null
    const value = cached?.key === key ? cached : readStorage()
    const age = Date.now() - value?.checkedAt
    if (value?.key !== key || typeof value.canAccess !== 'boolean' || !Number.isFinite(age) || age < 0 || age >= MAX_AGE) return null
    cached = value
    return value.canAccess
}

const notify = (key) => listeners.get(key)?.forEach((listener) => listener())

export function subscribeYearAnalyticsAccess(key, listener) {
    if (!listeners.has(key)) listeners.set(key, new Set())
    listeners.get(key).add(listener)
    return () => {
        const subscribers = listeners.get(key)
        subscribers?.delete(listener)
        if (!subscribers?.size) listeners.delete(key)
    }
}

export function clearYearAnalyticsAccess() {
    cached = null
    pending.clear() // Responses started before logout cannot repopulate the cache.
    writeStorage(null)
    listeners.forEach((_, key) => notify(key))
}

// Called synchronously by the auth setter, covering every logout entry point.
// The first authenticated user may reuse storage from a previous page load.
export function setYearAnalyticsUser(user) {
    const key = yearAnalyticsAccessKey(user)
    if (!key || (activeKey !== null && key !== activeKey)) clearYearAnalyticsAccess()
    activeKey = key
}

const confirmedAccess = (response) => {
    if (response?.status === 403) return false
    if (response?.status === 200 && typeof response.data?.canAccess === 'boolean') return response.data.canAccess
    return null
}

const saveAccess = (key, canAccess) => {
    cached = { key, canAccess, checkedAt: Date.now() }
    writeStorage(cached)
    notify(key)
}

// Search responses can revoke access immediately, including while a capability
// check is pending. Transport failures and malformed responses are not denials.
export function updateYearAnalyticsAccess(user, response) {
    const key = yearAnalyticsAccessKey(user)
    const canAccess = confirmedAccess(response)
    if (!key || canAccess === null || key !== activeKey) return
    pending.delete(key)
    saveAccess(key, canAccess)
}

export function refreshYearAnalyticsAccess(user, fetcher) {
    const key = yearAnalyticsAccessKey(user)
    if (!key) return Promise.resolve()
    if (pending.has(key)) return pending.get(key)
    const request = Promise.resolve()
        .then(fetcher)
        .then((response) => {
            if (pending.get(key) !== request) return
            const canAccess = confirmedAccess(response)
            if (canAccess !== null) saveAccess(key, canAccess)
        })
        .catch(() => { /* Keep the last confirmed value during transient failures. */ })
        .finally(() => {
            if (pending.get(key) === request) pending.delete(key)
        })
    pending.set(key, request)
    return request
}
